import i18next from 'i18next';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { tableHelper } from 'common/helpers/table/table-helper';
import { MasterLayout, MainLayout, ToolbarLayout, ToolbarLayoutLeft, ToolbarLayoutRight } from 'common/imports/content-layout';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { RolesDictionary } from 'common/helpers/dictionaries/roles-dictionary';
import { StatusFilterDropdown } from 'common/partials/StatusFilterDropdown';
import { DeleteModal } from 'common/partials/DeleteModal';
import ActionButton from 'common/partials/ActionButton';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { UserManagementModel as Model } from '../model/user-management.model';
import { UserManagementService as Service } from '../services/user-management.service';
import { StatusFilter } from 'common/types/status-filter.model';

export function UserManagement() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<Model[]>([]);
    const [filteredTableData, setFilteredTableData] = useState<Model[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRows, setSelectedRows] = useState<Model[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toggleCleared, setToggleCleared] = useState(false);
    const [deleteId, setDeleteId] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [status, setStatus] = useState<StatusFilter>(StatusFilter.Active);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(100);
    const [sorting, setSorting] = useState<{ orderBy?: string; order?: 'asc' | 'desc' }>({});

    const handleShowDeleteModal = (id: number) => {
        setShowDeleteModal(true);
        setDeleteId(id);
    };
    const handleCloseDeleteModal = () => setShowDeleteModal(false);

    const fetchData = async (incomingStatus?: StatusFilter) => {
        try {
            const currentStatus = incomingStatus != null ? incomingStatus : status;
            setStatus(currentStatus);
            setIsLoading(true);
            const response = await Service.getDataList({
                status: currentStatus,
                pageNumber: currentPage,
                email: searchTerm,
                name: searchTerm,
                userName: searchTerm,
                pageSize: rowsPerPage,
                orderBy: sorting.orderBy,
                order: sorting.order
            });

            if (response && !response.hasErrors) {
                setTableData(response.data.items);
                setFilteredTableData(response.data.items);
                setTotalRows(response.data.totalCount);
            }
        } catch (err) {
            toast.error(`${t('crud.read.errorMessage')}`);
        } finally {
            setIsLoading(false);
        }
    };

    const columns: TableColumn<Model>[] = [
        {
            name: 'Id',
            selector: (row) => row?.id || '',
            sortable: true,
            sortField: 'Id',
            width: '50px'
        },
        {
            name: t('table.name'),
            selector: (row) => row.name || '',
            sortable: true,
            sortField: 'Name'
        },
        {
            name: t('form.username'),
            selector: (row) => row.userName || '',
            sortable: true,
            sortField: 'UserName'
        },
        {
            name: t('table.email'),
            selector: (row) => row.email || '',
            sortable: true,
            sortField: 'Email'
        },
        {
            name: t('table.status'),
            cell: (row) =>
                row.isActive ? (
                    <span className="badge badge-light-primary">{t('generalMessages.active')}</span>
                ) : (
                    <span className="badge badge-light-danger">{t('generalMessages.inactive')}</span>
                ),

            width: '150px'
        },
        {
            name: `${t('table.actions')}`,
            cell: (row) => (
                <div className="d-grid d-flex gap-2">
                    <ActionButton type='edit' onClick={() => navigate(`/security/usermanagement/${row.id}`)} roles={[RolesDictionary.Security.UserManagement]} />
                    <ActionButton type='deactivate' onClick={() => handleShowDeleteModal(row.id!)} roles={[RolesDictionary.Security.UserManagement]} disabled={!row.isActive} />
                </div>
            ),

            width: '100px'
        },
    ];

    const handleRowSelected = useCallback((state: any) => {
        setSelectedRows(state.selectedRows);
    }, []);

    const deleteDataById = async () => {
        setIsLoading(true);
        const result = await Service.deleteUserById(deleteId);
        if (result && !result.hasErrors) {
            toast.success(`${t('crud.deactivate.successMessage')}`);
        } else {
            toast.error(`${t('crud.deactivate.errorMessage')}`);
        }
        setToggleCleared(!toggleCleared);
        handleCloseDeleteModal();
        fetchData();
        setIsLoading(false);
    };

    const contextActions = useMemo(() => {
        const deleteSelectedRows = () => {
            setShowDeleteModal(true);
            selectedRows.forEach((row) => {
                setDeleteId(row.id!);
            });
        }

        return (
            <button type="button" className="btn btn-sm btn-danger" onClick={deleteSelectedRows}>
                {t('crud.deactivate.buttonSave')}
            </button>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, toggleCleared]);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, rowsPerPage, sorting, searchTerm]);

    const handleListSort = (selectedColumn: TableColumn<Model>, sortDirection: 'asc' | 'desc') => {
        setTableData([]);
        setSorting({
            orderBy: selectedColumn.sortField,
            order: sortDirection
        });
    };

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight>
                    <div className="d-flex align-items-center gap-2">
                        <StatusFilterDropdown handleFilterData={fetchData} />
                        <Link to="/security/usermanagement/add" className="btn btn-sm btn-primary fw-bolder">
                            {t('addUserButton')}
                        </Link>
                    </div>
                </ToolbarLayoutRight>
            </ToolbarLayout>
            <MainLayout>
                <div className="card card-flush">
                    <div className="card-body">
                        <DataTable
                            title={tableHelper.TableHeader((e: any) => setSearchTerm(e.target.value))}
                            persistTableHead={true}
                            columns={columns}
                            data={filteredTableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            selectableRows
                            selectableRowsHighlight
                            dense
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                            paginationPerPage={rowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            onChangePage={(page) => setCurrentPage(page)}
                            onChangeRowsPerPage={(newPerPage, page) => {
                                setRowsPerPage(newPerPage);
                                setCurrentPage(page);
                            }}
                            highlightOnHover
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            contextActions={contextActions}
                            contextMessage={tableHelper.ContextMessage()}
                            onSelectedRowsChange={handleRowSelected}
                            clearSelectedRows={toggleCleared}
                            selectableRowsVisibleOnly={true}
                            customStyles={itLabTableStyle}
                            onSort={handleListSort}
                            sortServer
                        />
                    </div>
                </div>

                <DeleteModal
                    type='deactivate'
                    handleDelete={deleteDataById}
                    onClose={handleCloseDeleteModal}
                    open={showDeleteModal}
                />
            </MainLayout>
        </MasterLayout>
    );
}
